(ns hai.tasks.page
  (:require
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as data]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.ui :as tasks-ui]
   [hai.tasks.view :as view]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.search :as collection-search]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.kanban :as kanban]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.add-menu :as add-menu]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))
(def ^:private edit-icon (icons/mui-icon :outlined :edit))
(def ^:private share-icon (icons/mui-icon :outlined :person_add))
(def ^:private trash-icon (icons/mui-icon :outlined :delete))

(defsub ::selected [db]
  (get db ::selected #{}))

(defsub ::selected? [db task-uuid]
  (contains? (get db ::selected #{}) task-uuid))

(defevent ::toggle-selected [db task-uuid]
  (let [selected (get db ::selected #{})]
    (assoc db ::selected (if (contains? selected task-uuid)
                           (disj selected task-uuid)
                           (conj selected task-uuid)))))

(defevent ::clear-selected [db]
  (dissoc db ::selected))

(defsub ::selected-tasks
  [selected <- [::selected]
   tasks    <- [::data/list]]
  (filterv #(contains? selected (:task/uuid %)) tasks))

;; Bare mutation forms (not `{(mutation …) [out]}` joins): EQL collapses
;; several joins that share a dispatch key, so only one would run. Bare calls
;; each execute. Mirrors the shared collection bulk-action.
(defevent-fx ::bulk-complete [{:keys [db]} task-uuids]
  ;; The completed-archive collection is only mounted (and registered under
  ;; `:collections`) while its section is expanded — resolving it while
  ;; collapsed would throw "No table found".
  {:db (dissoc db ::selected)
   :resolve {:query (mapv (fn [task-uuid]
                            (list 'task/complete {:task/uuid task-uuid}))
                          task-uuids)
             :on-success-fx (cond-> [[:dispatch [::data/load-all-my-tasks]]]
                              (::completed-open? db)
                              (conj [:dispatch [:table/resolve ::completed]]))}})

(defevent-fx ::bulk-delete [{:keys [db]} task-uuids]
  {:db (dissoc db ::selected)
   :resolve {:query (mapv (fn [task-uuid]
                            (list 'task/delete {:task/uuid task-uuid}))
                          task-uuids)
             :on-success-fx [[:dispatch [::data/load-all-my-tasks]]]}})

(o/defstyled task-avatar :span
  :inline-flex :items-center :justify-center :flex-shrink-0
  {:width          "26px"
   :height         "26px"
   :border-radius  "50%"
   :background     t/--card-cream
   :color          t/--card-forest
   :font-size      "11px"
   :font-weight    "700"
   :letter-spacing "0.02em"
   :user-select    "none"}
  [:svg {:width "16px" :height "16px"}]
  ;; Current user: hollow ring instead of a fill — a shape distinction that
  ;; survives grayscale and doesn't fight the row's sage hover tint.
  [:&.me
   {:background "transparent"
    :box-shadow "inset 0 0 0 1px var(--task-accent)"
    :color      t/--task-accent}])

(defn- assignee-avatar [assignee]
  (let [me?      (and assignee (data/assignee-me? @(<s :auth/identity) assignee))
        initials (data/assignee-initials assignee)]
    [task-avatar {:title (or (data/assignee-name assignee) "Unassigned")
                  :class (when me? "me")}
     (or initials  [person-icon])]))

;; ---------------------------------------------------------------------------
;; Now / Next / Later columns — the same compact row look as the Completed
;; archive below, one column per deadline bucket.
;; ---------------------------------------------------------------------------

(o/defstyled task-row-title :span
  :block :cursor-pointer
  {:color         t/--card-forest
   :font-size     "14px"
   :line-height   "1.35"
   :overflow-wrap "anywhere"})

(o/defstyled task-row :div
  :flex :flex-row :items-center
  {:gap "12px" :padding "10px 14px"
   :background t/--colour-snow
   :border-radius "10px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-10
   :transition "border-color 0.15s ease, background 0.15s ease"}
  [:&:hover {:border-color t/--card-sage :background t/--card-sage-soft-15}]
  ;; Fixed min-height reserves room for the deadline line so cards stay the same
  ;; height whether or not the task has a deadline.
  [:.row-body
   :flex :flex-col :justify-center
   {:flex "1 1 auto" :min-width "0" :min-height "36px" :gap "2px"}]
  [view/task-deadline {:font-size "11px" :line-height "1.3"}
   [:svg {:width "13px" :height "13px"}]]
  [task-avatar {:flex-shrink "0"}]
  [:.row-menu {:width "28px" :height "28px" :flex-shrink "0"}]
  [:.row-confirm
   {:display "flex" :align-items "center" :flex-shrink "0"
    :gap "8px" :font-size "13px" :color t/--card-forest}]
  [:.confirm-prompt {:color t/--link-muted}]
  [:.delete-yes
   {:padding "6px 14px"
    :border "none" :border-radius "8px"
    :background t/--property-13 :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--property-14}]]
  [:.delete-no
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :font-family "inherit"
    :font-size "13px" :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

;; Card renderer for the kanban board. The collection card-render hook always
;; calls `(render collection row)` with a form-wrapped row, so we take that shape
;; directly and unwrap `:form/current-value` here - there is no separate adapter
;; shim ([HAI-001-05]; the unwrap is structural to the card hook, not removable).
(defn- task-row-view [_collection row]
  (r/with-let [!confirming? (r/atom false)
               !share-open? (r/atom false)
               !editing?    (r/atom false)]
    (let [task            (:form/current-value row)
          task-uuid       (:task/uuid task)
          title           (:task/title task)
          description     (:task/description task)
          links           (:task/activities task)
          link-urls       (vec (keep :activity/url links))
          can-edit?       (sharing/can-edit? task)
          can-delete?     (sharing/can-delete? task)
          confirming?     @!confirming?
          editing?        @!editing?
          completed-open? @(<s [::completed-open?])
          ;; The new :query board ([HAI-001-04]) renders from `:collections`, not
          ;; the `::data/tasks` slice these mutations optimistically patch. For
          ;; instant feedback ([HAI-001-07]) each per-task action optimistically
          ;; patches the board's rows *now* (`:table/patch-row`/`:table/remove-row`),
          ;; then this success re-resolve reconciles against server truth - the
          ;; authority that also self-heals a rejected mutation (`/resolve` answers
          ;; 200 on rejection, so `:on-success-fx` runs and re-fetches). The board
          ;; is always mounted here, so this re-resolve is unconditional (unlike
          ;; the archive's `::completed`, which is only registered while expanded,
          ;; and unlike create's cross-surface `:table/resolve-if-mounted`).
          resolve-board-fx [[:dispatch [:table/resolve data/board-collection-id]]]
          open            (fn [] (>e [:routing/navigate [:tasks/detail task]]))
          complete        (fn [_]
                             ;; Optimistic: drop the card from the board now (it is an
                             ;; open-tasks board, so a completed task leaves it), then
                             ;; reconcile. Re-resolve the board always; the archive
                             ;; `::completed` only while expanded (resolving it
                             ;; collapsed throws "No table found").
                            (>e [:table/remove-row data/board-collection-id :task/uuid task-uuid])
                            (>e [::data/complete task-uuid
                                 (cond-> resolve-board-fx
                                   completed-open? (conj [:dispatch [:table/resolve ::completed]]))]))
          save-edit       (fn [{:keys [title description urgency deadline urls assignee]}]
                            (let [clear-deadline?  (and (:task/deadline-at task)
                                                        (nil? (:date deadline)))
                                  deadline-params  (deadline/value->params deadline)]
                              ;; Optimistic instant feedback on the board (mirrors the
                              ;; patch `::data/update` applies to `::tasks`): show the
                              ;; edited title/urgency/deadline now - an urgency change
                              ;; re-buckets the card's lane immediately. The success
                              ;; re-resolve below reconciles against server truth.
                              (>e [:table/patch-row data/board-collection-id :task/uuid task-uuid
                                   (cond-> {:task/title title :task/urgency urgency}
                                     (some? description) (assoc :task/description description)
                                     deadline-params     (merge deadline-params)
                                     clear-deadline?     (assoc :task/deadline-at nil
                                                                :task/deadline-all-day? nil
                                                                :task/deadline-timezone nil))])
                              (>e [::data/update {:task-uuid task-uuid
                                                  :title title
                                                  :description description
                                                  :urgency urgency
                                                  :deadline deadline
                                                  :urls urls
                                                  :clear-deadline? clear-deadline?}
                                   resolve-board-fx]))
                            ;; Reassignment goes through its own `task/assign`
                            ;; mutation, and only when a contact was picked. Kept
                            ;; non-optimistic to mirror the bespoke board (whose
                            ;; `::data/assign` is not optimistic); the success
                            ;; re-resolve reflects the new assignee.
                            (when (:contact/uuid assignee)
                              (>e [::data/assign task-uuid assignee resolve-board-fx]))
                            (reset! !editing? false))
          menu-actions (cond-> []
                         can-edit?   (conj ["Edit" (fn [_] (reset! !editing? true)) edit-icon]
                                           ["Share" (fn [_] (reset! !share-open? true)) share-icon])
                         can-delete? (conj ["Delete" (fn [_] (reset! !confirming? true)) trash-icon]))]
      (if editing?
        [tasks-ui/quick-add {:class "editing"}
         [tasks-ui/task-form {:form-id [::edit-form task-uuid]
                              :initial-value (cond-> {:task/title title
                                                      :task/urgency (get task :task/urgency :task.urgency/later)}
                                               (seq description) (assoc :task/description description)
                                               (:task/deadline-at task) (assoc :task/deadline (deadline/from-task task))
                                               (seq link-urls) (assoc :activity/urls link-urls))
                              :assignable? true
                              :initial-assignee (:task/assigned-to task)
                              :auto-focus? true
                              :placeholder "Title"
                              :primary-label "save"
                              :on-save save-edit
                              :on-cancel #(reset! !editing? false)}]]
        [task-row
         [tasks-ui/stampbox {:task-title (:task/title task)
                             :disabled?  (not can-edit?)
                             :on-toggle  complete}]
         [:div.row-body
          [task-row-title
           {:role        "button"
            :tab-index   0
            :on-click    (when-not confirming? open)
            :on-key-down (fn [e]
                           (when (and (not confirming?)
                                      (or (= "Enter" (.-key e)) (= " " (.-key e))))
                             (.preventDefault e)
                             (open)))}
           (:task/title task)]
          (when (:task/deadline-at task)
            [view/task-deadline {:task task}])]
         (if confirming?
           [:span.row-confirm
            [:span.confirm-prompt "Delete?"]
            [:button.delete-no
             {:type "button"
              :on-click (fn [_] (reset! !confirming? false))}
             "Cancel"]
            [:button.delete-yes
             {:type "button"
              :on-click (fn [_]
                          (reset! !confirming? false)
                          ;; Optimistic: drop the card now; the success re-resolve
                          ;; reconciles (and self-heals a rejected delete).
                          (>e [:table/remove-row data/board-collection-id :task/uuid task-uuid])
                          (>e [::data/delete task-uuid resolve-board-fx]))}
             "Delete"]]
           [:<>
            [assignee-picker/component
             {:task-uuid task-uuid
              :editable? can-edit?
              ;; Mirror the picker's default assign, but re-resolve the new
              ;; board too (its default dispatches `::data/assign` with no
              ;; success fx, so the :query board would miss the reassignment).
              :on-pick   (fn [contact]
                           (>e [::data/assign task-uuid
                                {:contact/uuid (:contact/uuid contact)}
                                resolve-board-fx]))
              :trigger   [assignee-avatar (:task/assigned-to task)]}]
            [:span.row-menu
             (when can-edit?
               [:span [c/DialogRoot
                       {:open         @!share-open?
                        :onOpenChange (fn [open?]
                                        (reset! !share-open? open?)
                                        (when-not open?
                                          (>e [::data/load-all-my-tasks])))}
                       [c/DialogContent
                        [sharing.components/sharing-dialog-title]
                        [c/dialog-close-x]
                        [c/DialogDescription "People with access to this task"]
                        [sharing.components/permissions-table task]]]
                [table/more-menu-cell* menu-actions]])]])]))))

(o/defstyled bulk-controls-bar :div
  :flex :items-center
  {:gap "12px"}
  [:.count {:font-size "14px" :font-weight "600" :color t/--card-forest}]
  [:.clear
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :color t/--card-forest-70 :font-family "inherit"
    :font-size "13px" :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

(defn- bulk-confirm-dialog [!confirm]
  (let [{:keys [action tasks]} @!confirm
        n       (count tasks)
        delete? (= action :delete)]
    [c/AlertDialogRoot
     {:open         (some? @!confirm)
      :onOpenChange (fn [open?] (when-not open? (reset! !confirm nil)))}
     [c/AlertDialogContent
      [c/AlertDialogTitle (if delete? "Delete tasks" "Complete tasks")]
      [c/AlertDialogDescription
       (if delete?
         (str "Delete " n " task" (when (not= 1 n) "s") "? This can't be undone.")
         (str "Mark " n " task" (when (not= 1 n) "s") " complete?"))]
      [c/Flex {:justify "end" :gap "3" :mt "4"}
       [c/Button {:variant  "soft" :color "gray"
                  :on-click #(reset! !confirm nil)}
        "Cancel"]
       [c/Button {:variant  "solid"
                  :color    (if delete? "red" "green")
                  :on-click (fn []
                              (>e [(if delete? ::bulk-delete ::bulk-complete)
                                   (mapv :task/uuid tasks)])
                              (reset! !confirm nil))}
        (if delete? "Delete" "Complete")]]]]))

(defn- bulk-controls []
  (r/with-let [!confirm (r/atom nil)]
    (let [selected-tasks @(<s [::selected-tasks])
          total          (count selected-tasks)
          completable    (filterv sharing/can-edit? selected-tasks)
          deletable      (filterv sharing/can-delete? selected-tasks)]
      [bulk-controls-bar
       (when (pos? total)
         [:<>
          [:span.count (str total " selected")]
          [c/DropdownMenuRoot
           [c/DropdownMenuTrigger
            [c/Button {:variant "soft" :color "gray"}
             "Bulk actions" [c/DropdownMenuTriggerIcon]]]
           [c/DropdownMenuContent
            (when (seq completable)
              [c/DropdownMenuItem
               {:onSelect (fn [_] (reset! !confirm {:action :complete :tasks completable}))}
               (str "Mark complete (" (count completable) ")")])
            (when (seq deletable)
              [c/DropdownMenuItem
               {:color    "red"
                :onSelect (fn [_] (reset! !confirm {:action :delete :tasks deletable}))}
               (str "Delete (" (count deletable) ")")])]]
          [:button.clear {:on-click #(>e [::clear-selected])} "Clear"]
          [bulk-confirm-dialog !confirm]])])))

;; ---------------------------------------------------------------------------
;; Completed archive — server-paginated + searchable via the collection
;; component, lazily mounted so nothing loads until the section is opened.
;; ---------------------------------------------------------------------------

(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_right))

(defsub ::completed-open? [db]
  (get db ::completed-open? false))

(defevent ::toggle-completed [db]
  (update db ::completed-open? not))

(defevent-fx ::uncomplete-archived [_ task-uuid]
  {:resolve {:query [{(list 'task/uncomplete {:task/uuid task-uuid})
                      [:task/uuid]}]
             :on-success-fx [[:dispatch [:table/resolve ::completed]]
                             [:dispatch [::data/load-all-my-tasks]]]}})

(defn- completed-when [completed-at]
  (when completed-at
    (.toLocaleDateString (js/Date. completed-at)
                         "en-US" #js {:month "short" :day "numeric"})))

(o/defstyled completed-title-link :span
  :cursor-pointer :block
  {:text-decoration "line-through"
   :color           t/--card-forest-70
   :font-size       "14px"
   :overflow-wrap   "anywhere"}
  [:&:hover {:color t/--card-forest}])

(o/defstyled completed-time-label :span
  {:white-space "nowrap" :font-size "13px" :color t/--card-forest-70})

(o/defstyled completed-row :div
  :flex :flex-row :items-center
  {:gap "12px" :padding "10px 14px"
   :background t/--colour-snow
   :border-radius "10px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-10
   :transition "border-color 0.15s ease, background 0.15s ease"}
  [:&:hover {:border-color t/--card-sage :background t/--card-sage-soft-15}]
  [completed-title-link {:flex "1 1 auto" :min-width "0"}]
  [completed-time-label {:flex-shrink "0"}]
  [task-avatar {:flex-shrink "0"}])

(defn- completed-card [_collection item]
  (let [task     (:form/current-value item)
        allowed? (sharing/can-edit? task)
        open     (fn [] (>e [:routing/navigate [:tasks/detail task]]))]
    [completed-row
     [tasks-ui/stampbox {:done?      true
                         :task-title (:task/title task)
                         :disabled?  (not allowed?)
                         :on-toggle  #(>e [::uncomplete-archived (:task/uuid task)])}]
     [completed-title-link
      {:role        "button"
       :tab-index   0
       :on-click    (fn [_] (open))
       :on-key-down (fn [e]
                      (when (or (= "Enter" (.-key e)) (= " " (.-key e)))
                        (.preventDefault e)
                        (open)))}
      (:task/title task)]
     [completed-time-label (completed-when (:task/completed-at task))]
     [assignee-picker/component
      {:task-uuid (:task/uuid task)
       :editable? allowed?
       :trigger   [assignee-avatar (:task/assigned-to task)]}]]))

(o/defstyled completed-wrap :div
  [card/solution-grid {:grid-template-columns "1fr" :gap "8px"}])

(defn- completed-collection []
  [completed-wrap
   [collection/component
    {:id           ::completed
     :query        [{:user/completed-tasks
                     [{:collection/items
                       [:task/uuid :task/title :task/completed-at
                        :task/assigned-to :user.entity/permissions]}]}]
     :paginate?    true
     :pagination   {:table/per-page 10 :direction :desc :sort-attrs [:task/completed-at]}
     :default-view :collection.view/cards
     :controls     (fn [table] [collection-search/component table])
     :card         completed-card
     :no-items     (fn [_] [c/Text {:color "gray"} [:em "No completed tasks yet."]])
     :columns      []}]])

(o/defstyled completed-toggle :button
  :flex :items-center :cursor-pointer
  {:gap         "8px"
   :margin-top  "36px"
   :background  "transparent"
   :border      "none"
   :padding     "0"
   :font-family "inherit"
   :font-size   "18px"
   :font-weight "700"
   :color       t/--card-forest}
  [:svg {:width "22px" :height "22px" :fill t/--card-forest-70
         :transition "transform 0.15s ease"}]
  [:&.open [:svg {:transform "rotate(90deg)"}]])

(defn- completed-section []
  (let [open? @(<s [::completed-open?])]
    [:section
     [completed-toggle {:class (when open? "open")
                        :on-click #(>e [::toggle-completed])}
      [chevron-icon] "Completed"]
     (when open?
       [:div {:style {:margin-top "16px"}}
        [completed-collection]])]))

;; ---------------------------------------------------------------------------
;; New tasks board ([HAI-001-04]) — the bespoke Now/Next/Later columns ported
;; onto the generic `:collection.view/kanban` view. It is a `:query` collection
;; so the collection's own (server-side) search drives it, formalising what
;; `::data/load-all-my-tasks` already did (a 500-row open-tasks fetch, newest
;; first). Rendered ALONGSIDE the bespoke board below for the HITL parity
;; review; the bespoke board, its search, and the `task-row-view` card adapter
;; are removed only after sign-off ([HAI-001-05]).
;; ---------------------------------------------------------------------------

(def ^:private board-lanes
  "Now / Next / Later columns for the tasks board (mirrors `data/bucket-tabs`).
   Later is the catch-all, so untriaged / nil-urgency tasks land there exactly
   as the bespoke board does through `data/task-bucket`."
  [{:collection.view.kanban.lane/id :now   :collection.view.kanban.lane/label "Now"}
   {:collection.view.kanban.lane/id :next  :collection.view.kanban.lane/label "Next"}
   {:collection.view.kanban.lane/id :later :collection.view.kanban.lane/label "Later"
    :collection.view.kanban.lane/catch-all? true}])

(o/defstyled board-search :div
  "Brand theming for the generic collection search on the tasks board so it reads
   as the bespoke `search-input` exactly ([HAI-001-11]). Consumer-side by design:
   the brand tokens (forest / sage / snow) stay here in web-app and the shared
   `collection/search` component keeps its neutral design-system look - this
   wrapper just restyles the radix `TextField` descendants that component renders.
   Neutralises the radix surface variant's own box-shadow outline and applies a
   real 1px border with the same tokens as the bespoke `search-input`, so the two
   read identically."
  ;; Match the bespoke board's search-to-lanes spacing: the collection wrapper
  ;; leaves ~12px below the search, the bespoke `controls-row` leaves ~28px, so
  ;; add the 16px difference here.
  {:margin-bottom "16px"}
  [:.rt-TextFieldRoot
   {:flex          "0 1 520px"
    :max-width     "520px"
    ;; radix size-2 pins the root to 32px; let padding + the input's line-height
    ;; drive height so it matches the bespoke input's 39px exactly.
    :height        "auto"
    :padding       "8px 14px"
    :border-width  "1px"
    :border-style  "solid"
    :border-color  t/--card-forest-14
    :border-radius "8px"
    :background    t/--colour-snow
    :box-shadow    "none"
    ;; radix draws a 2px focus outline in its accent colour; suppress it and let
    ;; the sage border be the only focus affordance, exactly as the bespoke input.
    :outline       "none"
    :transition    "border-color 0.15s"}
   [:&:focus-within {:border-color t/--card-sage}]]
  [:.rt-TextFieldInput
   {;; radix ships the input a 0.5px bottom padding; zero it (the 8px/14px lives
    ;; on the root) so the box height lands on the bespoke's 39px, not 40px.
    :padding     "0"
    :color       t/--card-forest
    :font-size   "14px"
    :font-family "inherit"}
   [:&::placeholder {:color t/--card-forest-70}]])

(defn- tasks-board
  "The tasks board as a generic `:query` kanban collection. Reproduces
   `::data/load-all-my-tasks`'s fetch — open tasks only, newest first, capped at
   500 — with the full `data/task-pattern` under `:collection/items` so avatars,
   permissions, deadlines and urgency bucketing all resolve. `data/task-bucket`
   buckets into the declared lanes; `task-row-view` is the card renderer - it
   takes the form-wrapped row straight from the card-render hook and unwraps it
   itself (no separate adapter)."
  []
  [collection/component
   {:id           data/board-collection-id
    :query        [{(list :user.tasks/table {:filters {:task/open? true}})
                    [{:collection/items data/task-pattern}]}]
    :pagination   {:table/per-page 500 :sort-attrs [:task/created-at] :direction :desc}
    :default-view :collection.view/kanban
    :view-options [kanban/view-option]
    :collection.view.kanban/lanes     board-lanes
    :collection.view.kanban/bucket-fn data/task-bucket
    :card         task-row-view
    :controls     (fn [table] [board-search [collection-search/component table]])
    :search       {:placeholder "Search tasks…"}
    :columns      []}])

(defn page [_route]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/lead*
    [layout/lead "Tasks are grouped into Now, Next, and Later by their deadline."]
    [:div.buttons
     [button/dark-small {:on-click #(>e [::add-menu/open-task-modal])
                         :text     "Add task"
                         :icon     [icons/plus]}]]]
   [layout/content
    [tasks-board]
    ;; The bulk-selection subsystem is dormant/dead ([HAI-001-05] reports it;
    ;; deletion is deferred to another doc). Its only mount point was the bespoke
    ;; `controls-row`; keep it mounted here so it is neither deleted nor left an
    ;; unreferenced orphan - it renders nothing until `::selected` is populated
    ;; (which no UI does).
    [bulk-controls]
    [completed-section]]])
